import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus,
  MessageSquare,
  Star,
  Trash2,
  Settings,
  Crown,
  Sparkles,
  Library,
  X,
  User,
} from "lucide-react";
import StoneLogo from "@/components/StoneLogo";

const SidebarBtn = ({ icon: Icon, children, onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
  >
    <Icon size={15} strokeWidth={1.5} />
    {children}
  </button>
);

const ChatSidebar = ({
  open,
  onClose,
  conversations,
  activeId,
  onNewChat,
  onSelect,
  onDelete,
  onToggleFavorite,
}) => {
  const [filter, setFilter] = useState("all");

  const filtered = conversations.filter((c) =>
    filter === "favorites" ? c.favorite : true
  );

  if (!open) return null;

  return (
    <aside
      className="w-[280px] shrink-0 border-r border-[#555555] bg-[#2D2D2D] flex flex-col h-screen"
      data-testid="chat-sidebar"
    >
      <div className="px-4 pt-6 pb-5 flex items-start justify-between">
        <Link to="/" data-testid="sidebar-logo-link">
          <StoneLogo size={56} wordmarkSize={30} subtitle="Inteligência Mineral" />
        </Link>
        <button
          onClick={onClose}
          className="text-zinc-500 hover:text-white p-1 rounded hover:bg-white/5 mt-1"
          data-testid="close-sidebar-btn"
        >
          <X size={16} />
        </button>
      </div>

      <div className="px-3">
        <button
          onClick={onNewChat}
          data-testid="new-chat-btn"
          className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl border border-[#262626] hover:border-[#3a3a3a] hover:bg-white/[0.02] transition text-[13px] text-zinc-100 font-medium"
        >
          <Plus size={15} strokeWidth={1.6} />
          Novo Chat
        </button>
      </div>

      <div className="px-3 mt-5 flex gap-1">
        <button
          onClick={() => setFilter("all")}
          data-testid="filter-all-btn"
          className={`flex-1 text-[11px] tracking-[0.18em] uppercase py-1.5 rounded-md transition ${
            filter === "all"
              ? "bg-white/5 text-white"
              : "text-zinc-500 hover:text-zinc-300"
          }`}
        >
          Todas
        </button>
        <button
          onClick={() => setFilter("favorites")}
          data-testid="filter-favorites-btn"
          className={`flex-1 text-[11px] tracking-[0.18em] uppercase py-1.5 rounded-md transition ${
            filter === "favorites"
              ? "bg-white/5 text-white"
              : "text-zinc-500 hover:text-zinc-300"
          }`}
        >
          Favoritos
        </button>
      </div>

      <div className="flex-1 overflow-y-auto scrollbar-thin mt-3 px-2" data-testid="conversation-list">
        {filtered.length === 0 && (
          <div className="text-center text-[12px] text-zinc-600 py-10 px-4 leading-relaxed">
            Nenhuma conversa ainda. <br/>Comece pelo botão Novo Chat.
          </div>
        )}
        {filtered.map((c) => {
          const active = c.id === activeId;
          return (
            <div
              key={c.id}
              data-testid={`conv-item-${c.id}`}
              className={`group rounded-lg px-2 py-2 mb-0.5 transition cursor-pointer flex items-center gap-2 ${
                active ? "bg-[#4A4A4A]" : "hover:bg-[#404040]"
              }`}
              onClick={() => onSelect(c.id)}
            >
              <MessageSquare
                size={13}
                strokeWidth={1.5}
                className={active ? "text-white" : "text-zinc-500"}
              />
              <div className="flex-1 min-w-0">
                <div className={`text-[13px] truncate ${active ? "text-white" : "text-zinc-300"}`}>
                  {c.title || "Nova conversa"}
                </div>
              </div>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleFavorite(c.id, c.favorite);
                }}
                data-testid={`fav-toggle-${c.id}`}
                className={`opacity-0 group-hover:opacity-100 p-1 rounded transition ${
                  c.favorite ? "opacity-100 text-amber-300" : "text-zinc-500 hover:text-white"
                }`}
              >
                <Star size={12} fill={c.favorite ? "currentColor" : "none"} strokeWidth={1.5} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (confirm("Remover esta conversa?")) onDelete(c.id);
                }}
                data-testid={`delete-conv-${c.id}`}
                className="opacity-0 group-hover:opacity-100 p-1 rounded text-zinc-500 hover:text-red-400 transition"
              >
                <Trash2 size={12} strokeWidth={1.5} />
              </button>
            </div>
          );
        })}
      </div>

      <div className="border-t border-[#555555] p-3 space-y-1">
        <Link to="/library" data-testid="sidebar-library-link">
          <SidebarBtn icon={Library} testid="sidebar-library-btn">Biblioteca Mineral</SidebarBtn>
        </Link>
        <SidebarBtn icon={Sparkles} testid="sidebar-compare-btn">Comparar Materiais</SidebarBtn>
        <SidebarBtn icon={Settings} testid="sidebar-settings-btn">Configurações</SidebarBtn>
      </div>

      <div className="p-3 border-t border-[#555555]">
        <Link
          to="/pro"
          data-testid="sidebar-pro-cta"
          className="block rounded-xl border border-[#555555] bg-[#3F3F3F] p-3 hover:border-[#C9A84C]/60 transition"
        >
          <div className="flex items-center gap-2 mb-1">
            <Crown size={14} className="text-[#C9A84C]" />
            <div className="text-[12.5px] text-white font-medium">StoneIA Pro</div>
          </div>
          <div className="text-[11.5px] text-zinc-500 leading-relaxed">
            Conversas ilimitadas + visão avançada
          </div>
        </Link>
        <div className="mt-3 flex items-center gap-2 px-1" data-testid="sidebar-user">
          <div className="w-7 h-7 rounded-full bg-[#262626] flex items-center justify-center text-zinc-300">
            <User size={13} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[12px] text-zinc-200 truncate">Convidado</div>
            <div className="text-[10.5px] text-zinc-500 truncate">Plano Free</div>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default ChatSidebar;
